/**
 * Landing-surface domain types (TO-454). The public landing is served by
 * landing-service; these are the shared access-mode vocabulary (ADR-0051) and
 * the claims carried by a visitor's proof-of-access session cookie.
 */

/**
 * The four canonical landing access modes (ADR-0051, supersedes ADR-0010 Part 2).
 * These are the only modes; the names here are canonical.
 *
 * - `NO_AUTH` — no verification; anyone with the link views the landing.
 * - `CODE` — the visitor enters a shared code set out-of-band by the sender.
 * - `OTP` — a one-time code delivered by SMS (channel dep: TO-383).
 * - `MAGIC_LINK` — a signed single-use link delivered by email; possession is
 *   the proof (channel dep: TO-382).
 *
 * `CODE`, `OTP` and `MAGIC_LINK` are the *verifying* modes: a visitor is
 * challenged, proves access once, and is then issued a session (see
 * {@link LandingSessionClaims}). `OTP` / `MAGIC_LINK` proofs are issued to the
 * offer customer's primary mobile / email and redeemed once (TO-478).
 */
export enum AccessMode {
	NO_AUTH = 'NO_AUTH',
	CODE = 'CODE',
	OTP = 'OTP',
	MAGIC_LINK = 'MAGIC_LINK',
}

/** Stable error codes of the public landing challenge routes (TO-478). */
export enum LandingChallengeErrorCode {
	/** Any refused challenge / verify — deliberately one code for every reason (no oracle). */
	UNAUTHORIZED = 'UNAUTHORIZED',
	/** The offer's per-60 s or per-24 h challenge send limit is reached (`Retry-After` set). */
	LANDING_CHALLENGE_RATE_LIMITED = 'LANDING_CHALLENGE_RATE_LIMITED',
	/** The offer's CODE verification is locked after 3 consecutive wrong codes (`Retry-After` set, TO-164). */
	LANDING_CODE_LOCKED = 'LANDING_CODE_LOCKED',
}

/** The schema version of a {@link LandingSessionClaims} token. */
export const LANDING_SESSION_VERSION = 1

/**
 * Claims carried by a visitor's proof-of-access session token (TO-454).
 *
 * A verifying mode ({@link AccessMode.CODE} etc.) issues this once, after the
 * visitor passes the challenge, as an HMAC-signed cookie. On every later
 * request the gate re-verifies the signature and checks that `lid` matches the
 * landing in the URL and `exp` is still in the future — no server-side lookup.
 *
 * The schema is deliberately shaped to fit a *future* server-side validation
 * layer without a format change: `sid` is a unique per-session id (a fresh
 * uuidv7 minted on every token) that a server-side session store could key on
 * to look up or revoke a single session. The stateless cookie path we ship now
 * never reads `sid`; it is present so server-side validation can be added later
 * against the same token format, without forcing already-issued cookies to
 * re-challenge.
 */
export interface LandingSessionClaims {
	/** Schema version — {@link LANDING_SESSION_VERSION}. Lets the format evolve. */
	v: number
	/** Unique session id (uuidv7). Unused by the stateless path; the key a future server-side store would look up / revoke by. */
	sid: string
	/** Landing id this session grants access to. Checked against the URL on every request — a token for one landing cannot be replayed against another. */
	lid: string
	/** The access mode that granted this session. */
	mode: AccessMode
	/**
	 * The OTP / MAGIC_LINK challenge whose redemption issued this session (TO-164);
	 * absent for CODE (a shared code binds no identity) and for pre-TO-164 tokens.
	 */
	cid?: string
	/** Issued-at, epoch seconds. */
	iat: number
	/** Expiry, epoch seconds. */
	exp: number
}

/**
 * How a landing view was admitted (TO-164): the offer's access mode at serve
 * time and, for an OTP / MAGIC_LINK session, the id of the redeemed challenge.
 * Opaque ids only — never a phone, email or masked form of either.
 */
export interface LandingAccessTags {
	accessMode: AccessMode
	verifiedChallengeId?: string
}
